import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { isDevBypass, devUser, devSession, devModuleAccess } from '../lib/devAuth';
import type { PlatformRole } from '../types';

interface TbUser {
  id: string;
  auth_id: string | null;
  email: string;
  full_name: string;
  display_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  job_title: string | null;
  department: string | null;
  platform_role: PlatformRole;
  status: string;
  invited_by: string | null;
  last_login_at: string | null;
  metadata: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ModuleAccess {
  id: string;
  user_id: string;
  module_key: string;
  access_level: string;
  is_active: boolean;
}

interface ProjectDeny {
  project_id: string;
}

interface AuthState {
  user: TbUser | null;
  session: unknown;
  moduleAccess: ModuleAccess[];
  loading: boolean;
  isDevMode: boolean;
  initialized: boolean;
  projectDenies: ProjectDeny[];
  loadProjectDenies: () => Promise<void>;
  initialize: () => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isAuthenticated: () => boolean;
  isPlatformOwner: () => boolean;
  isSuperAdmin: () => boolean;
  isAdmin: () => boolean;
  isAdminOrAbove: () => boolean;
  canAccessModule: (moduleKey: string) => boolean;
  canEditProject: (project: { id?: string; created_by?: string | null } | null | undefined) => boolean;
  canDeleteProject: (project: { id?: string; created_by?: string | null } | null | undefined) => boolean;
  isOwnerOf: (project: { created_by?: string | null } | null | undefined) => boolean;
}

async function loadProfile(authId: string) {
  const { data: profile } = await supabase
    .from('tb_users')
    .select('*')
    .eq('auth_id', authId)
    .maybeSingle();

  if (!profile) return null;

  const { data: access } = await supabase
    .from('tb_module_access')
    .select('*')
    .eq('user_id', profile.id)
    .eq('is_active', true);

  return { profile: profile as TbUser, access: (access || []) as ModuleAccess[] };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  moduleAccess: [],
  loading: true,
  isDevMode: false,
  initialized: false,
  projectDenies: [],

  loadProjectDenies: async () => {
    const { user } = get();
    if (!user?.id) {
      set({ projectDenies: [] });
      return;
    }
    const { data } = await supabase
      .from('cs_project_denied_users')
      .select('project_id')
      .eq('user_id', user.id);
    set({ projectDenies: (data || []) as ProjectDeny[] });
  },

  initialize: async () => {
    if (get().initialized) return;

    if (isDevBypass()) {
      set({
        user: devUser as TbUser,
        session: devSession,
        moduleAccess: devModuleAccess,
        loading: false,
        isDevMode: true,
        initialized: true,
      });
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const result = await loadProfile(session.user.id);
        if (result) {
          set({
            user: result.profile,
            session,
            moduleAccess: result.access,
            loading: false,
            initialized: true,
          });
          get().loadProjectDenies();
        } else {
          set({ session, loading: false, initialized: true });
        }
      } else {
        set({ loading: false, initialized: true });
      }
    } catch {
      set({ loading: false, initialized: true });
    }

    supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        if (event === 'SIGNED_OUT' || !session?.user) {
          set({ user: null, session: null, moduleAccess: [] });
          const cookieAuthEnabled =
            import.meta.env.VITE_AUTH_STORAGE === 'cookie' &&
            !!import.meta.env.VITE_AUTH_COOKIE_DOMAIN;
          if (cookieAuthEnabled) {
            window.location.href = 'https://home.abcotto.com';
          }
          return;
        }
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          const result = await loadProfile(session.user.id);
          if (result) {
            set({ user: result.profile, session, moduleAccess: result.access });
            get().loadProjectDenies();
          }
        }
      })();
    });
  },

  logout: async () => {
    await supabase.auth.signOut();
    set({ user: null, session: null, moduleAccess: [], isDevMode: false });
    const cookieAuthEnabled =
      import.meta.env.VITE_AUTH_STORAGE === 'cookie' &&
      !!import.meta.env.VITE_AUTH_COOKIE_DOMAIN;
    if (cookieAuthEnabled) {
      window.location.href = 'https://home.abcotto.com';
    }
  },

  refreshProfile: async () => {
    const { user, isDevMode } = get();
    if (isDevMode || !user?.auth_id) return;
    const result = await loadProfile(user.auth_id);
    if (result) {
      set({ user: result.profile, moduleAccess: result.access });
    }
    await supabase
      .from('tb_users')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', user.id);
  },

  isAuthenticated: () => {
    const { user, isDevMode } = get();
    return isDevMode || !!user;
  },

  isPlatformOwner: () => {
    const { user } = get();
    return user?.platform_role === 'platform_owner';
  },

  isSuperAdmin: () => {
    const { user } = get();
    return user?.platform_role === 'platform_owner' || user?.platform_role === 'super_admin';
  },

  isAdmin: () => {
    const { user } = get();
    return user?.platform_role === 'admin';
  },

  isAdminOrAbove: () => {
    const { user } = get();
    return (
      user?.platform_role === 'platform_owner' ||
      user?.platform_role === 'super_admin' ||
      user?.platform_role === 'admin'
    );
  },

  canAccessModule: (moduleKey: string) => {
    const { moduleAccess, user } = get();
    if (user?.platform_role === 'platform_owner' || user?.platform_role === 'super_admin') return true;
    return moduleAccess.some(
      (a) => a.module_key === moduleKey && a.is_active
    );
  },

  isOwnerOf: (project) => {
    const { user } = get();
    if (!project?.created_by || !user?.id) return false;
    return project.created_by === user.id;
  },

  canEditProject: (project) => {
    const { user, projectDenies } = get();
    if (!project || !user) return false;
    if (user.platform_role === 'platform_owner' || user.platform_role === 'super_admin') return true;
    if (project.created_by === user.id) return true;
    const projectId = (project as { id?: string }).id;
    if (!projectId) return true;
    return !projectDenies.some((d) => d.project_id === projectId);
  },

  canDeleteProject: (project) => {
    const { user } = get();
    if (!project || !user) return false;
    if (user.platform_role === 'platform_owner' || user.platform_role === 'super_admin') return true;
    return project.created_by === user.id;
  },
}));
